import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

import {
  applyConsent,
  logConsentEvent,
  readConsent,
  registerConsentSettingsGlobal,
  saveConsent,
} from "@/lib/consent";

const COPY = {
  sv: {
    title: "Cookies hos Tailor's Barber",
    body: "Vi använder nödvändiga cookies för att sajten ska fungera. Med ditt samtycke använder vi också cookies för statistik (Google Analytics) och marknadsföring (Google Ads), så att vi kan se vad som fungerar och mäta våra annonser. Du kan ändra ditt val när som helst via Cookieinställningar i sidfoten.",
    policy: "Läs mer i vår integritetspolicy",
    acceptAll: "Godkänn alla",
    necessaryOnly: "Endast nödvändiga",
    customise: "Anpassa",
    save: "Spara val",
    necessary: "Nödvändiga",
    necessaryText: "Krävs för att sidan ska fungera, till exempel ditt cookieval.",
    analytics: "Statistik",
    analyticsText:
      "Google Analytics 4. Hjälper oss förstå hur sajten används. Cookies: _ga, _ga_*. Lagras upp till 2 år.",
    marketing: "Marknadsföring",
    marketingText:
      "Google Ads. Mäter om våra annonser leder till bokningar och gör att annonser kan anpassas. Cookies: _gcl_au, _gcl_aw. Lagras upp till 90 dagar.",
    always: "Alltid på",
  },
  en: {
    title: "Cookies at Tailor's Barber",
    body: "We use necessary cookies to make the site work. With your consent we also use cookies for statistics (Google Analytics) and marketing (Google Ads), so we can see what works and measure our ads. You can change your choice at any time via Cookie settings in the footer.",
    policy: "Read more in our privacy policy",
    acceptAll: "Accept all",
    necessaryOnly: "Necessary only",
    customise: "Customise",
    save: "Save choices",
    necessary: "Necessary",
    necessaryText: "Required for the site to work, for example your cookie choice.",
    analytics: "Statistics",
    analyticsText:
      "Google Analytics 4. Helps us understand how the site is used. Cookies: _ga, _ga_*. Stored for up to 2 years.",
    marketing: "Marketing",
    marketingText:
      "Google Ads. Measures whether our ads lead to bookings and allows ads to be personalised. Cookies: _gcl_au, _gcl_aw. Stored for up to 90 days.",
    always: "Always on",
  },
} as const;

function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-border transition-colors ${
        checked ? "bg-brass-deep" : "bg-muted"
      } ${disabled ? "opacity-60" : ""}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-background transition-transform motion-reduce:transition-none ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export function ConsentBanner() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const t = COPY[pathname === "/en" || pathname.startsWith("/en/") ? "en" : "sv"];

  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const firstButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMounted(true);
    const existing = readConsent();
    if (!existing) {
      setOpen(true);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
    registerConsentSettingsGlobal();
    const onOpen = () => {
      const current = readConsent();
      setAnalytics(Boolean(current?.analytics));
      setMarketing(Boolean(current?.marketing));
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener("tb:consent-open", onOpen);
    return () => window.removeEventListener("tb:consent-open", onOpen);
  }, []);

  useEffect(() => {
    if (open) firstButton.current?.focus();
  }, [open, details]);

  if (!mounted || !open) return null;

  const choose = (a: boolean, m: boolean) => {
    const state = saveConsent({ analytics: a, marketing: m });
    applyConsent(state);
    logConsentEvent(state);
    setOpen(false);
    setDetails(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="tb-consent-title"
      aria-describedby="tb-consent-desc"
      className="fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[60] p-3 sm:inset-x-auto sm:left-5 sm:p-0 md:bottom-5"
    >
      <div className="card-elev w-full max-w-[440px] bg-background p-5 sm:p-6">
        <h2 id="tb-consent-title" className="text-lg font-semibold">
          {t.title}
        </h2>

        {!details ? (
          <>
            <p id="tb-consent-desc" className="mt-3 text-sm text-muted-foreground">
              {t.body}
            </p>
            <p className="mt-3 text-sm">
              <a href="/integritetspolicy/" className="underline hover:text-brass-deep">
                {t.policy}
              </a>
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                ref={firstButton}
                type="button"
                className="btn-primary flex-1 text-sm"
                onClick={() => choose(true, true)}
              >
                {t.acceptAll}
              </button>
              <button
                type="button"
                className="btn-primary flex-1 text-sm"
                onClick={() => choose(false, false)}
              >
                {t.necessaryOnly}
              </button>
              <button
                type="button"
                className="btn-outline flex-1 text-sm"
                onClick={() => setDetails(true)}
              >
                {t.customise}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">{t.necessary}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{t.necessaryText}</p>
                </div>
                <Switch checked disabled label={`${t.necessary}: ${t.always}`} />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">{t.analytics}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{t.analyticsText}</p>
                </div>
                <Switch checked={analytics} onChange={setAnalytics} label={t.analytics} />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">{t.marketing}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{t.marketingText}</p>
                </div>
                <Switch checked={marketing} onChange={setMarketing} label={t.marketing} />
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                ref={firstButton}
                type="button"
                className="btn-primary flex-1 text-sm"
                onClick={() => choose(analytics, marketing)}
              >
                {t.save}
              </button>
              <button
                type="button"
                className="btn-primary flex-1 text-sm"
                onClick={() => choose(true, true)}
              >
                {t.acceptAll}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
